'use client';

import { JsonLd } from '@/seo/JsonLd';
import type { Product } from '@/data/products';
import { SITE_DOMAIN, toAbsoluteUrl } from '@/utils/url';
import { getProductSeoMeta } from '@/seo/productSeoMeta';

export function ProductSEO({ product }: { product: Product }) {
  const url = toAbsoluteUrl(`/${product.slug}`);
  const { title, description } = getProductSeoMeta(product);

  const breadcrumb = [
    { name: 'Anasayfa', item: toAbsoluteUrl('/') },
    {
      name:
        product.category === 'Bondex Sünger'
          ? 'Bondex Sünger'
          : product.category === 'Bariyerli Bondex'
            ? 'Bariyerli Bondex'
            : 'Çift Bariyerli Bondex',
      item:
        product.category === 'Bondex Sünger'
          ? toAbsoluteUrl('/bondex-sunger')
          : product.category === 'Bariyerli Bondex'
            ? toAbsoluteUrl('/bariyerli-bondex-sunger')
            : product.category === 'Akustik Sünger'
              ? toAbsoluteUrl('/akustik-sunger')
              : toAbsoluteUrl('/cift-bariyerli-bondex-sunger'),
    },
    { name: product.name, item: url },
  ];

  const schemaGraph: any[] = [
    {
      '@type': 'WebSite',
      '@id': `${toAbsoluteUrl('/')}#website`,
      name: 'Bondex Sünger',
      url: toAbsoluteUrl('/'),
      inLanguage: 'tr-TR',
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${toAbsoluteUrl('/')}urunler?q={search_term_string}`,
        },
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@type': 'Organization',
      '@id': `${toAbsoluteUrl('/')}#organization`,
      name: 'bariyerlibondexsunger.com',
      url: toAbsoluteUrl('/'),
      logo: {
        '@type': 'ImageObject',
        url: toAbsoluteUrl('/images/bondex-logo.webp'),
        width: 512,
        height: 512,
      },
      contactPoint: [
        {
          '@type': 'ContactPoint',
          telephone: '+90 216 519 34 11',
          contactType: 'customer service',
          areaServed: 'TR',
          availableLanguage: ['tr'],
        },
        {
          '@type': 'ContactPoint',
          telephone: '+90 546 919 11 34',
          contactType: 'sales',
          areaServed: 'TR',
          availableLanguage: ['tr'],
        },
      ],
      sameAs: [
        'https://www.facebook.com/profile.php?id=61575206163572',
        'https://x.com/KabinPods',
        'https://www.instagram.com/kabinpods/',
        'https://www.youtube.com/channel/UCKk3RjOabrJb8JQYK9BjWkw',
        'https://www.linkedin.com/company/tasar%C4%B1m-akustik-mimarl%C4%B1k',
      ],
    },
    {
      '@type': 'WebPage',
      '@id': `${url}#webpage`,
      url,
      name: title,
      description,
      isPartOf: { '@id': `${toAbsoluteUrl('/')}#website` },
      breadcrumb: { '@id': `${url}#breadcrumb` },
      inLanguage: 'tr-TR',
      datePublished: '2025-12-17',
      dateModified: new Date().toISOString().split('T')[0],
      primaryImageOfPage: {
        '@type': 'ImageObject',
        url: toAbsoluteUrl(product.imagePath),
        width: 1200,
        height: 750,
      },
      about: [
        { '@type': 'Thing', name: product.category },
        { '@type': 'Thing', name: 'Bondex sünger' },
        ...(product.category === 'Bariyerli Bondex' || product.category === 'Çift Bariyerli Bondex'
          ? [
              { '@type': 'Thing', name: 'Bariyerli Bondex Sünger' },
              { '@type': 'Thing', name: 'bariyerli bondex sünger' },
              { '@type': 'Thing', name: 'Bariyerli Bondex' },
              { '@type': 'Thing', name: 'bariyerli bondex' },
              { '@type': 'Thing', name: 'bariyerli ses yalıtım süngeri' },
            ]
          : []),
        ...(product.thicknessMm
          ? [
              { '@type': 'Thing', name: `${product.thicknessMm}mm Bondex Sünger` },
              { '@type': 'Thing', name: `Bondex sünger ${product.thicknessMm}mm` },
              { '@type': 'Thing', name: `ses yalıtım süngeri ${product.thicknessMm}mm` },
              ...(product.category === 'Bariyerli Bondex'
                ? [
                    { '@type': 'Thing', name: `Bariyerli Bondex Sünger ${product.thicknessMm}mm` },
                    { '@type': 'Thing', name: `bariyerli bondex sünger ${product.thicknessMm}mm` },
                    { '@type': 'Thing', name: `Bariyerli Bondex ${product.thicknessMm}mm` },
                  ]
                : []),
            ]
          : []),
        { '@type': 'Thing', name: 'ses yalıtım süngeri' },
        { '@type': 'Thing', name: 'akustik sünger' },
        { '@type': 'Thing', name: 'izolasyon süngeri' },
      ],
      speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: ['.ProductSEO', '.ProductPage h1'],
      },
      mainEntity: { '@id': `${url}#product` },
    },
    {
      '@type': 'BreadcrumbList',
      '@id': `${url}#breadcrumb`,
      itemListElement: breadcrumb.map((b, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        name: b.name,
        item: b.item,
      })),
    },
    {
      '@type': 'Product',
      '@id': `${url}#product`,
      name: product.name,
      image: [
        {
          '@type': 'ImageObject',
          url: toAbsoluteUrl(product.imagePath),
          width: 1200,
          height: 750,
        },
      ],
      description: product.description,
      brand: { '@type': 'Brand', name: 'Bondex' },
      category: product.category,
      url,
      sku: product.sku,
      mpn: product.mpn,
      productID: product.sku || product.mpn,
      material: 'Rebonded Süngerpan',
      audienceType: 'Business',
      // gtin: product.gtin, // Eğer GTIN numarası varsa eklenebilir
      ...(product.thicknessMm && {
        additionalProperty: [
          {
            '@type': 'PropertyValue',
            name: 'Kalınlık',
            value: `${product.thicknessMm}mm`,
            valueReference: {
              '@type': 'QuantitativeValue',
              value: product.thicknessMm,
              unitCode: 'MMT',
            },
          },
          {
            '@type': 'PropertyValue',
            name: 'Kategori',
            value: product.category,
          },
          {
            '@type': 'PropertyValue',
            name: 'Tip',
            value: 'Rebonded Süngerpan',
          },
        ],
      }),
      ...(!product.thicknessMm && {
        additionalProperty: [
          {
            '@type': 'PropertyValue',
            name: 'Kategori',
            value: product.category,
          },
          {
            '@type': 'PropertyValue',
            name: 'Tip',
            value: 'Rebonded Süngerpan',
          },
        ],
      }),
    },
  ];

  const productNode = schemaGraph.find((n) => n['@type'] === 'Product' && n['@id'] === `${url}#product`);

  if (product.priceTRY) {
    const validFromStr = new Date().toISOString().split('T')[0];
    // Calculate priceValidUntil (1 year from now)
    const priceValidUntil = new Date();
    priceValidUntil.setFullYear(priceValidUntil.getFullYear() + 1);
    const priceValidUntilStr = priceValidUntil.toISOString().split('T')[0];

    const offer: any = {
      '@type': 'Offer',
      url,
      priceCurrency: 'TRY',
      price: String(product.priceTRY),
      validFrom: validFromStr,
      priceValidUntil: priceValidUntilStr,
      availability: 'https://schema.org/InStock',
      itemCondition: 'https://schema.org/NewCondition',
      seller: { '@id': `${toAbsoluteUrl('/')}#organization` },
      hasMerchantReturnPolicy: { '@id': `${url}#merchantReturnPolicy` },
      shippingDetails: { '@id': `${url}#shippingDetails` },
    };

    // Add unitText if priceUnit is specified
    if (product.priceUnit === 'm2') {
      offer.unitText = 'm²';
      offer.priceSpecification = {
        '@type': 'UnitPriceSpecification',
        price: String(product.priceTRY),
        priceCurrency: 'TRY',
        unitCode: 'MTK', // Square meter
        unitText: 'm²',
      };
    }

    productNode.offers = offer;

    // Add MerchantReturnPolicy schema
    schemaGraph.push({
      '@type': 'MerchantReturnPolicy',
      '@id': `${url}#merchantReturnPolicy`,
      applicableCountry: {
        '@type': 'Country',
        name: 'TR',
      },
      returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
      merchantReturnDays: 14,
      returnMethod: 'https://schema.org/ReturnByMail',
      returnFees: 'https://schema.org/FreeReturn',
    });

    // Add OfferShippingDetails schema
    schemaGraph.push({
      '@type': 'OfferShippingDetails',
      '@id': `${url}#shippingDetails`,
      shippingRate: {
        '@type': 'MonetaryAmount',
        value: '0',
        currency: 'TRY',
      },
      shippingDestination: {
        '@type': 'DefinedRegion',
        addressCountry: {
          '@type': 'Country',
          name: 'TR',
        },
      },
      deliveryTime: {
        '@type': 'ShippingDeliveryTime',
        handlingTime: {
          '@type': 'QuantitativeValue',
          minValue: 1,
          maxValue: 3,
          unitCode: 'DAY',
        },
        transitTime: {
          '@type': 'QuantitativeValue',
          minValue: 1,
          maxValue: 5,
          unitCode: 'DAY',
        },
        businessDays: {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        },
        cutoffTime: '14:00',
      },
    });
  }

  if (product.reviews && product.reviews.length > 0) {
    const reviewCount = product.reviews.length;
    const ratingValue =
      product.reviews.reduce((sum, r) => sum + (Number.isFinite(r.ratingValue) ? r.ratingValue : 0), 0) / reviewCount;

    productNode.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: ratingValue.toFixed(1),
      reviewCount,
      bestRating: '5',
      worstRating: '1',
    };

    productNode.review = product.reviews.map((r) => ({
      '@type': 'Review',
      author: { '@type': 'Person', name: r.authorName },
      datePublished: r.datePublished,
      reviewBody: r.body,
      headline: r.title,
      ...(r.verified && { reviewAspect: 'Verification' }),
      reviewRating: {
        '@type': 'Rating',
        ratingValue: String(r.ratingValue),
        bestRating: '5',
        worstRating: '1',
      },
    }));
  }

  if (product.faqs && product.faqs.length > 0) {
    schemaGraph.push({
      '@type': 'FAQPage',
      '@id': `${url}#faq`,
      mainEntity: product.faqs.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
      })),
    });
  }

  const schema = {
    '@context': 'https://schema.org',
    '@graph': schemaGraph,
  };

  return (
    <JsonLd data={schema} />
  );
}
